import { posthogProvider } from "./posthog-provider";
import type {
  AnalyticsEventProps,
  AnalyticsProvider,
  AnalyticsSessionContext,
  GlobalAnalyticsContext,
} from "./types";

export const createSessionTracking = (providers: AnalyticsProvider[]) => {
  let currentSessionId: string | undefined;
  let globalContext: GlobalAnalyticsContext | undefined;

  return {
    /**
     * Sets the app session id (SessionShotsContext's sessionId) merged into
     * every subsequent start/end/track call as `appSessionId`, so every
     * event from one round can be filtered/grouped together in the
     * analytics tool — independent of the tool's own session concept.
     */
    setCurrentSessionId: (sessionId: string) => {
      currentSessionId = sessionId;
    },
    /**
     * Sets app-wide context (course, device, country, consent) merged into
     * every subsequent start/end/track call. Call whenever client config
     * changes (see ClientConfigContext) so events always reflect current
     * settings, not just those at session start.
     */
    setGlobalContext: (context: GlobalAnalyticsContext) => {
      globalContext = context;
    },
    start: (context: AnalyticsSessionContext) => {
      const merged = {
        ...globalContext,
        ...context,
        appSessionId: currentSessionId,
      };
      providers.forEach((provider) => provider.startSession(merged));
    },
    end: (context: AnalyticsSessionContext) => {
      const merged = {
        ...globalContext,
        ...context,
        appSessionId: currentSessionId,
      };
      providers.forEach((provider) => provider.endSession(merged));
    },
    track: (name: string, props?: AnalyticsEventProps) => {
      const merged = {
        ...globalContext,
        ...props,
        appSessionId: currentSessionId,
      };
      providers.forEach((provider) => provider.trackEvent(name, merged));
    },
  };
};

/**
 * Single entry point for app-level session start/end tracking. Call this
 * from wherever a session actually starts/ends (SessionShotsContext), never
 * inline at individual UI call sites — that's how the app's session stayed
 * decoupled from PostHog's own autocapture-based session concept.
 */
export const sessionTracking = createSessionTracking([posthogProvider]);
